import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { useIsMobile } from "@/hooks/useIsMobile";
import { BottomSheet, BottomSheetContent, ignoreToastInteraction } from "./bottom-sheet";

/** Set by `<Dialog alert>`; read by the parts below. */
const AlertContext = createContext(false);

/** True inside an `alert` dialog on a phone: the parts draw an iOS alert. */
function usePhoneAlert() {
  const alert = useContext(AlertContext);
  return useIsMobile() && alert;
}

/**
 * Phones get the shared bottom sheet's root (vaul), so `DialogContent` can drag.
 * `alert` is for a yes/no confirmation (iOS `.alert`): it stays a centred
 * dialog on phones too, with no X and no tap-outside dismissal there.
 */
function Dialog({ alert = false, ...props }: React.ComponentProps<typeof DialogPrimitive.Root> & { alert?: boolean }) {
  const isMobile = useIsMobile();
  return (
    <AlertContext.Provider value={alert}>
      {isMobile && !alert ? <BottomSheet {...props} /> : <DialogPrimitive.Root {...props} />}
    </AlertContext.Provider>
  );
}
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-[200] bg-overlay backdrop-blur-[2px]",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=open]:duration-200 data-[state=closed]:duration-150",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  onCloseAutoFocus,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
  // Phones: every dialog but an alert is the shared bottom sheet (one look, drag to close).
  const isMobile = useIsMobile();
  const alert = useContext(AlertContext);
  const phoneAlert = isMobile && alert;
  if (isMobile && !alert) {
    return (
      <BottomSheetContent
        className={className}
        onCloseAutoFocus={onCloseAutoFocus}
        onInteractOutside={onInteractOutside}
        {...props}
      >
        {children}
      </BottomSheetContent>
    );
  }
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        aria-describedby={undefined}
        role={alert ? "alertdialog" : undefined}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          onCloseAutoFocus?.(e);
        }}
        onInteractOutside={(e) => {
          ignoreToastInteraction(e);
          onInteractOutside?.(e);
        }}
        className={cn(
          "fixed left-1/2 top-1/2 z-[200] -translate-x-1/2 -translate-y-1/2",
          "w-[420px] max-w-[calc(100vw-2rem)]",
          "bg-canvas border border-border rounded-[14px]",
          // Repeated for :focus-visible: the panel takes focus on open, and the
          // global ring (meant for controls) would otherwise replace the shadow.
          "shadow-[0_20px_60px_var(--color-shadow-heavy),0_4px_12px_var(--color-shadow)]",
          "focus-visible:shadow-[0_20px_60px_var(--color-shadow-heavy),0_4px_12px_var(--color-shadow)]",
          "overflow-y-auto max-h-[calc(100dvh-2rem)] font-chrome",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
          "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          "data-[state=open]:duration-200 data-[state=closed]:duration-150",
          className,
          phoneAlert && "w-[calc(100vw-3rem)] max-w-[300px] rounded-[22px] border-0 text-center",
        )}
        {...props}
        // An alert on a phone is answered, not tapped away.
        onPointerDownOutside={(e) => {
          if (phoneAlert) e.preventDefault();
          props.onPointerDownOutside?.(e);
        }}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  // Phones: the X sits 12px from the top of the sheet, as from its right edge.
  const isMobile = useIsMobile();
  const phoneAlert = usePhoneAlert();
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 border-b border-border-light font-chrome",
        isMobile ? "p-3" : "px-4 py-2.5",
        className,
        phoneAlert && "justify-center border-b-0 px-4 pb-0 pt-5",
      )}
      {...props}
    >
      {children}
      {!phoneAlert && <DialogCloseButton />}
    </div>
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  const phoneAlert = usePhoneAlert();
  return (
    <DialogPrimitive.Title
      className={cn(
        "flex items-center gap-2 text-[13px] font-semibold text-ink-secondary font-chrome",
        className,
        phoneAlert && "justify-center text-center text-[16px] text-ink",
      )}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  // A phone alert: two buttons side by side, three stacked with Cancel last.
  const phoneAlert = usePhoneAlert();
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 px-4 py-3 border-t border-border-light bg-notelist font-chrome",
        className,
        phoneAlert &&
          "items-stretch border-t-0 bg-transparent px-4 pb-4 pt-1 *:min-h-10 *:flex-1 *:text-sm has-[>:nth-child(3)]:flex-col-reverse",
      )}
      {...props}
    />
  );
}

/**
 * The one X for every dialog and sheet: a round outlined button, like the
 * native sheets' close button. `lg` is the phone-sheet size.
 */
function DialogCloseButton({
  onClick,
  size = "md",
  className,
}: {
  onClick?: () => void;
  size?: "md" | "lg";
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <DialogClose asChild>
      <Button
        variant="outline"
        size="icon"
        onClick={onClick}
        aria-label={t("common.close")}
        title={t("common.close")}
        className={cn("shrink-0", size === "md" && "h-8 w-8", className)}
      >
        <Icon name="close" size={size === "md" ? 14 : 16} />
      </Button>
    </DialogClose>
  );
}

export {
  Dialog,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
